import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Users, Calendar, MessageCircle, Plus, Shield, Zap, Globe, Briefcase } from "lucide-react"

export function HomePage() {
  const features = [
    {
      icon: Users,
      title: "Beta Access",
      description: "Join our beta program and get early access to new features",
      href: "/beta",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: Calendar,
      title: "Book Demo",
      description: "Schedule a personalized demo with our team",
      href: "/demo",
      color: "from-green-500 to-green-600"
    },
    {
      icon: MessageCircle,
      title: "Contact Us",
      description: "Get in touch with us",
      href: "/contact",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: Plus,
      title: "Request Integration",
      description: "Request new integrations for your workflow",
      href: "/request",
      color: "from-pink-500 to-pink-600"
    },
    {
      icon: Briefcase,
      title: "Join Our Team",
      description: "Apply for open positions at 3 Halves Labs",
      href: "/careers",
      color: "from-orange-500 to-orange-600"
    }
  ]

  const benefits = [
    {
      icon: Shield,
      title: "Secure & Reliable",
      description: "Enterprise-grade security with 99.9% uptime guarantee"
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Process forms instantly with our optimized infrastructure"
    },
    {
      icon: Globe,
      title: "Global Scale",
      description: "Handle millions of form submissions across the globe"
    }
  ]

  return (
    <div className="space-y-16">
      {/* Hero Section */}
      <section className="text-center space-y-8">
        <div className="space-y-4">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-indigo-600 bg-clip-text text-transparent">
            Process Forms Like Never Before
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            A powerful middleware application that seamlessly processes multiple form types and integrates with your favorite third-party services. Available with your Score platform.
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
            <Link to="/beta" className="flex items-center space-x-2">
              <Users className="h-5 w-5" />
              <span>Join Beta</span>
            </Link>
          </Button>
          <Button size="lg" variant="outline" className="border-2">
            <Link to="/demo" className="flex items-center space-x-2">
              <Calendar className="h-5 w-5" />
              <span>Book Demo</span>
            </Link>
          </Button>
        </div>
      </section>

      {/* Features Grid */}
      <section className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            Choose Your Path
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Multiple ways to engage with our platform
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {features.map((feature) => {
            const Icon = feature.icon
            return (
              <Card key={feature.title} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 glass-morphism border-white/20">
                <CardHeader className="text-center">
                  <div className={`mx-auto h-12 w-12 rounded-lg bg-gradient-to-r ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                  <CardDescription>{feature.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild className="w-full" variant="outline">
                    <Link to={feature.href}>Get Started</Link>
                  </Button>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            Why Choose Score Forms?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Built for modern businesses that need reliable form processing
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {benefits.map((benefit) => {
            const Icon = benefit.icon
            return (
              <div key={benefit.title} className="text-center space-y-4">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                  <Icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold">{benefit.title}</h3>
                <p className="text-muted-foreground">{benefit.description}</p>
              </div>
            )
          })}
        </div>
      </section>
    </div>
  )
}